import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import axios, { AxiosError, AxiosHeaders, type AxiosResponse } from 'axios';
import { toast } from 'sonner';
import { USER_ROLES } from '../../../shared/constants/user.constants';
import { generateLink } from '../../../shared/services/app.shared-service';
import { translateLocaleSentence } from '../../../shared/services/locale.shared-service';
import type { ErrorResponse } from '../../../shared/types/error';
import { getErrorDetails } from '../../../shared/utils/error.utils';
import i18n from '../config/locale';
import {
	ACCESS_TOKEN_PREFIX,
	ACCESS_TOKEN_STORAGE_KEY,
	USER_STORAGE_KEY,
} from '../constants/auth.constants';
import { useAuthStore as authStore } from '../stores/useAuthStore';
import { SERVER_ROUTES } from './../../../shared/constants/routes.constants';
import { type RefreshTokenResponseSchemaType } from './../../../shared/schemas/authSchemas/authResponses.schema';
import { createIdbAsyncStorage, idbStore } from './api-cache';

const NO_RETRY_STATUS_CODES = [422, 429, 401];

function retryFunction(failureCount: number, error: unknown): boolean {
	if (!navigator.onLine) {
		return false;
	}
	if (axios.isAxiosError(error)) {
		const status = error.response?.status;
		// Don't retry if 422, 429, or 401
		if (NO_RETRY_STATUS_CODES.includes(status ?? 0)) return false;
	}
	// Retry up to 2 times for other errors
	return failureCount < 2;
}

function handleGlobalError(error: unknown) {
	const typedError = error as AxiosError<ErrorResponse>;
	if (axios.isAxiosError(typedError) && typedError.response?.status === 401) {
		authStore.getState().removeAuthenticated();
		toast.error(i18n.t('client.api.sessionExpired'));
		return;
	}
	if (typedError?.response?.data.error?.message) {
		toast.error(
			translateLocaleSentence(
				typedError?.response?.data.error?.message,
				i18n.t
			),
			{
				description: getErrorDetails(
					typedError?.response?.data?.error?.details,
					i18n.t
				),
			}
		);
	} else {
		toast.error(i18n.t('client.api.unexpectedError'));
	}
}

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: retryFunction,
			retryDelay: 1000,
			staleTime: 5 * 60 * 1000, // data fresh for 5 minutes
			throwOnError: (error, query) => {
				if (query.state.status === 'error') {
					handleGlobalError(error);
				}
				return false;
			},
		},
		mutations: {
			retry: retryFunction,
			retryDelay: 1000,
			onError: (error: unknown) => {
				handleGlobalError(error);
			},
		},
	},
});

export const asyncStoragePersister = createAsyncStoragePersister({
	storage: createIdbAsyncStorage(idbStore),
	serialize: (data) => JSON.stringify(data),
	deserialize: (data) => JSON.parse(data),
});

export const axiosClient = axios.create({
	baseURL: generateLink({
		baseUrl: import.meta.env.VITE_API_BASE_URL || '',
		route: [SERVER_ROUTES.BASE],
	}),
	timeout: 10_000,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	},
});

// Flag to track token refreshing
let isRefreshing = false;
let refreshQueue: Array<() => void> = [];

async function postValidateAndRetry(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	response: AxiosResponse<any, any>
): Promise<AxiosResponse> {
	const userRole = authStore.getState().user?.role ?? USER_ROLES.GUEST;
	const responseUserRole =
		(response.data as { userRole?: string }).userRole ?? USER_ROLES.GUEST;

	if (userRole === responseUserRole) return response;

	const originalRequest = response.config;

	if (isRefreshing) {
		return new Promise((resolve, reject) => {
			refreshQueue.push(async () => {
				const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
				if (!token) {
					reject(new Error('No token after refresh'));
					return;
				}

				// Rebuild and retry request
				if (originalRequest.headers instanceof AxiosHeaders) {
					originalRequest.headers.set(
						'Authorization',
						`${ACCESS_TOKEN_PREFIX} ${token}`
					);
				} else {
					(originalRequest.headers as Record<string, string>)[
						'Authorization'
					] = `${ACCESS_TOKEN_PREFIX} ${token}`;
				}

				try {
					const retriedResponse = await axiosClient(originalRequest);
					resolve(retriedResponse);
				} catch (err) {
					reject(err);
				}
			});
		});
	}

	isRefreshing = true;

	try {
		const refreshResponse = await axios.get<RefreshTokenResponseSchemaType>(
			generateLink({
				baseUrl: import.meta.env.VITE_API_BASE_URL || '',
				route: [
					SERVER_ROUTES.BASE,
					SERVER_ROUTES.AUTH.version,
					SERVER_ROUTES.AUTH.BASE,
					SERVER_ROUTES.AUTH.ROUTES.REFRESH_TOKEN,
				],
			}),
			{ withCredentials: true }
		);

		const newAccessToken = refreshResponse.data.accessToken;

		localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, newAccessToken);

		// Rebuild and retry current request
		if (originalRequest.headers instanceof AxiosHeaders) {
			originalRequest.headers.set(
				'Authorization',
				`${ACCESS_TOKEN_PREFIX} ${newAccessToken}`
			);
		} else {
			(originalRequest.headers as Record<string, string>)[
				'Authorization'
			] = `${ACCESS_TOKEN_PREFIX} ${newAccessToken}`;
		}

		const retriedResponse = await axiosClient(originalRequest);

		return retriedResponse;
	} catch (error) {
		localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
		localStorage.removeItem(USER_STORAGE_KEY);
		authStore.getState().removeAuthenticated();
		isRefreshing = false;
		// Process queued requests
		refreshQueue = [];
		toast.error(i18n.t('client.api.sessionExpired'));
	} finally {
		isRefreshing = false;
		// Process queued requests
		refreshQueue.forEach((cb) => cb());
		refreshQueue = [];
	}
	return response; // Fallback return, should not reach here
}

// Request interceptor → inject token
axiosClient.interceptors.request.use((config) => {
	const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
	if (token && config.headers) {
		config.headers.Authorization = `${ACCESS_TOKEN_PREFIX} ${token}`;
	}
	return config;
});

// Response interceptor → catch 401 errors
axiosClient.interceptors.response.use(
	async (response) => {
		const method = response.config.method?.toLowerCase();

		if (method === 'get') {
			return await postValidateAndRetry(response);
		}

		return response;
	},
	async (error) => {
		const originalRequest = error.config;

		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			if (!isRefreshing) {
				isRefreshing = true;

				try {
					const response =
						await axios.get<RefreshTokenResponseSchemaType>(
							generateLink({
								baseUrl:
									import.meta.env.VITE_API_BASE_URL || '',
								route: [
									SERVER_ROUTES.BASE,
									SERVER_ROUTES.AUTH.version,
									SERVER_ROUTES.AUTH.BASE,
									SERVER_ROUTES.AUTH.ROUTES.REFRESH_TOKEN,
								],
							}),
							{ withCredentials: true }
						);

					const newAccessToken = response.data.accessToken;

					// Store new access token
					localStorage.setItem(
						ACCESS_TOKEN_STORAGE_KEY,
						newAccessToken
					);

					// Retry any queued requests
					refreshQueue.forEach((callback) => callback());
					refreshQueue = [];

					// Retry the original request
					originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
					return axiosClient(originalRequest);
				} catch {
					localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
					localStorage.removeItem(USER_STORAGE_KEY);
					refreshQueue = [];
					isRefreshing = false;
					authStore.getState().removeAuthenticated();
					return Promise.reject(error);
				} finally {
					isRefreshing = false;
				}
			} else {
				// Queue this request until the refresh finishes
				return new Promise((resolve, reject) => {
					refreshQueue.push(async () => {
						const token = localStorage.getItem(
							ACCESS_TOKEN_STORAGE_KEY
						);
						if (!token) {
							reject(error);
							return;
						}
						originalRequest.headers.Authorization = `${ACCESS_TOKEN_PREFIX} ${token}`;
						try {
							const result = await axiosClient(originalRequest);
							resolve(result);
						} catch (err) {
							reject(err);
						}
					});
				});
			}
		}

		return Promise.reject(error);
	}
);
